/**
 * The portal shell.
 *
 * Role comes from the JWT the Worker verified, and it decides what the UI
 * offers — but never what the UI is allowed to do. Every write goes through the
 * API, which checks the caller's project role against the table, and RLS
 * decides what any read returns. A user who forged `canWrite` in a debugger
 * would find every mutation refused.
 */

import type { JSX } from 'react';
import { Suspense, lazy, useCallback, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

import { loadSession as loadClientSession } from './client/client-api.js';
import { AuthCallback, RelayMark, SignIn, useAuth } from './components/Auth.js';
import { ThemeToggle } from './components/Theme.js';
import { api, type ProjectSummary } from './lib/api.js';
import { signOut } from './lib/supabase.js';

const Queue = lazy(async () => ({ default: (await import('./pages/Queue.js')).Queue }));
const BoardPage = lazy(async () => ({ default: (await import('./pages/BoardPage.js')).BoardPage }));
const TicketDetailPage = lazy(async () => ({
  default: (await import('./pages/TicketDetail.js')).TicketDetailPage,
}));
const ProjectStatusPage = lazy(async () => ({
  default: (await import('./pages/ProjectStatus.js')).ProjectStatusPage,
}));
// Its own bundle: a client signing in has no use for the board, the queue or
// anything else the staff portal loads.
const ClientPortal = lazy(async () => ({ default: (await import('./client/ClientPortal.js')).ClientPortal }));
const DomainsPage = lazy(async () => ({ default: (await import('./pages/Domains.js')).Domains }));
const UsersPage = lazy(async () => ({ default: (await import('./pages/Users.js')).Users }));
const NewProjectPage = lazy(async () => ({
  default: (await import('./pages/NewProject.js')).NewProjectPage,
}));
const NewTicketPage = lazy(async () => ({
  default: (await import('./pages/NewTicket.js')).NewTicketPage,
}));
const Analytics = lazy(async () => ({ default: (await import('./pages/Analytics.js')).Analytics }));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: (attempt, error) => {
        // An authorisation failure will not fix itself, and retrying it three
        // times just delays the sign-in prompt.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return attempt < 2;
      },
    },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Outside `Authenticated`, which is the staff session. A client signs
            in here with their own password and never meets Cloudflare Access. */}
        <Route
          path="/client/*"
          element={
            <Suspense fallback={<div className="signin"><span className="spinner" aria-hidden="true" /></div>}>
              <ClientPortal />
            </Suspense>
          }
        />
        <Route path="*" element={<Authenticated />} />
      </Routes>
    </QueryClientProvider>
  );
}

function Authenticated(): JSX.Element {
  const auth = useAuth();

  if (auth.status === 'loading') {
    return (
      <div className="signin">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (auth.status === 'signed_out') {
    // A client who signed in earlier has no staff session, and their reports
    // are what they came back for.
    return loadClientSession() !== null ? <Navigate to="/client" replace /> : <SignIn />;
  }

  return <Shell />;
}

function Shell(): JSX.Element {
  const location = useLocation();
  const page = pageFor(location.pathname);

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.projects() });

  const canWrite = me.data?.globalRole === 'admin' || me.data?.globalRole === 'triager';
  const isAdmin = me.data?.globalRole === 'admin';
  const projectList = projects.data?.projects ?? [];
  const [railed, setRailed] = useRail();

  return (
    <div className="shell" data-nav={railed ? 'rail' : 'full'}>
      <a href="#relay-content" className="skip-link">
        Skip to content
      </a>

      <nav className="sidebar" aria-label="Main">
        <div className="sidebar-brand">
          <RelayMark size={19} />
          <span className="sidebar-brand-name">Relay</span>
          {/*
            Collapsing is a real preference, not a responsive guess: the queue
            is a dense table that wants every pixel, and somebody who lives in
            it all day should be able to say so once. The projects list is what
            the width is spent on, so the rail hides that and keeps the pages.
          */}
          <button
            type="button"
            className="icon-button sidebar-collapse"
            aria-expanded={!railed}
            aria-label={railed ? 'Expand the sidebar' : 'Collapse the sidebar'}
            title={railed ? 'Expand the sidebar' : 'Collapse the sidebar'}
            onClick={() => setRailed(!railed)}
          >
            <RailIcon railed={railed} />
          </button>
        </div>

        <NavLink to="/queue" className="nav-item" title="Queue">
          <QueueIcon />
          <span className="nav-label">Queue</span>
        </NavLink>
        <NavLink to="/analytics" className="nav-item" title="Analytics">
          <ChartIcon />
          <span className="nav-label">Analytics</span>
        </NavLink>
        {isAdmin ? (
          <NavLink to="/people" className="nav-item" title="People">
            <PeopleIcon />
            <span className="nav-label">People</span>
          </NavLink>
        ) : null}
        {isAdmin ? <DomainsNav /> : null}
        {canWrite ? (
          <NavLink to="/new" className="nav-item" title="Raise a ticket">
            <PlusIcon />
            <span className="nav-label">Raise a ticket</span>
          </NavLink>
        ) : null}

        <div className="sidebar-section sidebar-section-actions">
          <span>Projects</span>
          {isAdmin ? (
            <Link
              to="/projects/new"
              className="icon-button"
              title="Add a project"
              aria-label="Add a project"
            >
              <PlusIcon />
            </Link>
          ) : null}
        </div>

        {projects.isPending ? (
          <span className="field-hint" style={{ padding: '0 8px' }}>
            Loading…
          </span>
        ) : projectList.length === 0 ? (
          <span className="field-hint" style={{ padding: '0 8px' }}>
            No projects yet.
          </span>
        ) : (
          projectList.map((project) => (
            <ProjectNav key={project.id} project={project} isAdmin={isAdmin} />
          ))
        )}

        {isAdmin ? <ArchivedProjects /> : null}

        <div className="sidebar-foot">
          <ThemeToggle />
          <span className="sidebar-user" title={me.data?.email}>
            {me.data?.email}
          </span>
          <button
            type="button"
            className="button button-ghost"
            style={{ width: '100%' }}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </nav>

      <div className="main">
        <header className="page-head">
          <div>
            <h1>{page.title}</h1>
            <p>{page.blurb}</p>
          </div>
          <div className="page-head-actions">
            {me.data !== undefined && !canWrite ? (
              <span className="badge" title="You can see everything but change nothing">
                Read-only
              </span>
            ) : null}
          </div>
        </header>

        <main className="content" id="relay-content">
          {me.isError ? (
            <div className="error-banner" role="alert">
              {me.error instanceof Error ? me.error.message : 'Your account could not be loaded.'}
            </div>
          ) : null}

          <Suspense
            fallback={
              <p className="field-hint">
                <span className="spinner" aria-hidden="true" /> Loading…
              </p>
            }
          >
            <Routes>
              <Route path="/" element={<Navigate to="/queue" replace />} />
              <Route path="/queue" element={<Queue projects={projectList} canWrite={canWrite} />} />
              <Route path="/board/:projectId" element={<BoardPage canWrite={canWrite} />} />
              <Route path="/tickets/:ticketId" element={<TicketDetailPage canWrite={canWrite} />} />
              {/* Before the parameterised route, or "new" is matched as an id. */}
              {/* Administrators only, and the route is absent rather than
                  guarded for everyone else: a page that exists and refuses is
                  an invitation to find out what is on it. */}
              {isAdmin ? (
                <Route
                  path="/people"
                  element={<UsersPage currentUserId={me.data?.userId ?? null} />}
                />
              ) : null}
              {isAdmin ? <Route path="/domains" element={<DomainsPage />} /> : null}
              <Route path="/projects/new" element={<NewProjectPage />} />
              <Route
                path="/projects/:projectId"
                element={<ProjectStatusPage projects={projectList} isAdmin={isAdmin} />}
              />
              <Route path="/new" element={<NewTicketPage projects={projectList} />} />
              <Route path="/analytics" element={<Analytics projects={projectList} />} />
              <Route
                path="*"
                element={
                  <div className="empty">
                    <h3>Not found</h3>
                    <p>
                      <Link to="/queue">Back to the queue</Link>
                    </p>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/**
 * One project in the sidebar, and the sites under it.
 *
 * Collapsed by default, because a customer with five sites would otherwise
 * push every other customer off the screen. Expanding is local state rather
 * than a route: which projects you have open is not something worth putting in
 * a URL, and it should survive clicking between their boards.
 *
 * The project itself links to its whole board; a site links to the same board
 * filtered to that site. Both are the board, deliberately — a site is a filter
 * over a customer's work, not a separate backlog, and giving it a page of its
 * own would suggest otherwise.
 */
function ProjectNav(props: {
  readonly project: ProjectSummary;
  readonly isAdmin: boolean;
}): JSX.Element {
  const { project } = props;
  const [open, setOpen] = useState(false);
  const sites = project.sites;

  return (
    <div className="nav-project">
      <div className="nav-project-row">
        {sites.length > 0 ? (
          <button
            type="button"
            className="nav-disclosure"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} the sites under ${project.displayName}`}
            onClick={() => setOpen((current) => !current)}
          >
            <ChevronIcon open={open} />
          </button>
        ) : (
          // Keeps the names in one column whether or not a project has sites.
          <span className="nav-disclosure nav-disclosure-empty" aria-hidden="true" />
        )}

        <NavLink to={`/board/${project.id}`} className="nav-item nav-item-grow">
          <span>{project.displayName}</span>
          {/* Degradation is visible from the navigation, not only from the
              project page: nobody visits a project page to check whether it
              is healthy. */}
          {project.connectionStatus.widget.status === 'failed' ? (
            <span
              className="nav-alert"
              role="img"
              aria-label="Widget not reporting"
              title="The widget on this project has stopped reporting"
            />
          ) : null}
        </NavLink>

        {props.isAdmin ? (
          <Link
            to={`/projects/${project.id}`}
            className="icon-button"
            title={`Settings for ${project.displayName}`}
            aria-label={`Settings for ${project.displayName}`}
          >
            <CogIcon />
          </Link>
        ) : null}
      </div>

      {open ? (
        <ul className="nav-sites">
          {sites.map((site) => (
            <li key={site.id}>
              <NavLink to={`/board/${project.id}?site=${site.id}`} className="nav-item nav-site">
                <span>{site.label}</span>
                {site.kind !== 'production' ? <span className="badge">{site.kind}</span> : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Archived projects, behind a disclosure and fetched only when opened.
 *
 * Separate request rather than a flag on the main list: putting finished
 * customers in the sidebar beside current ones is the thing archiving exists
 * to stop. Not fetched until the section is opened, so an account with no
 * archived projects pays nothing for the option.
 */
function ArchivedProjects(): JSX.Element | null {
  const [open, setOpen] = useState(false);

  const archived = useQuery({
    queryKey: ['projects', 'archived'],
    queryFn: () => api.projects({ archived: true }),
    enabled: open,
  });

  const list = archived.data?.projects ?? [];

  return (
    <div className="nav-archived">
      <button
        type="button"
        className="nav-item nav-item-quiet"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <ChevronIcon open={open} />
        <span>Archived</span>
        {open && !archived.isPending ? <span className="nav-count">{list.length}</span> : null}
      </button>

      {open ? (
        archived.isPending ? (
          <span className="field-hint" style={{ padding: '0 8px 0 26px' }}>
            Loading…
          </span>
        ) : list.length === 0 ? (
          <span className="field-hint" style={{ padding: '0 8px 0 26px' }}>
            Nothing archived.
          </span>
        ) : (
          <ul className="nav-sites">
            {list.map((project) => (
              <li key={project.id}>
                {/* Straight to the settings page, which is where it can be
                    restored or deleted, and the only reason to open an
                    archived project at all. */}
                <Link to={`/projects/${project.id}`} className="nav-item nav-site">
                  <span>{project.displayName}</span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

/* Inline SVG rather than an icon font or a dependency: three icons is not a
   package, and `currentColor` means they follow the theme without a second
   set for dark mode. */

/**
 * Whether the sidebar is collapsed to a rail.
 *
 * Read once at mount and written on every change, wrapped because
 * `localStorage` throws rather than returning null in a browser with site
 * data blocked — the same reason the theme toggle wraps its own access.
 */
const RAIL_KEY = 'relay-nav-rail';

function storedRail(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === 'true';
  } catch {
    return false;
  }
}

function useRail(): readonly [boolean, (next: boolean) => void] {
  const [railed, set] = useState<boolean>(() => storedRail());

  const choose = useCallback((next: boolean) => {
    set(next);
    try {
      window.localStorage.setItem(RAIL_KEY, String(next));
    } catch {
      // A preference we cannot persist is still a preference for this tab.
    }
  }, []);

  return [railed, choose];
}

function QueueIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M2 9.5h3l1 2h4l1-2h3M2 9.5 3.8 3.4A1 1 0 0 1 4.76 2.7h6.48a1 1 0 0 1 .96.7L14 9.5v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-3Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function PeopleIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M6 8a2.4 2.4 0 1 0 0-4.8A2.4 2.4 0 0 0 6 8Zm-4 5.2c0-2 1.8-3.2 4-3.2s4 1.2 4 3.2M11 8.2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 1.8c1.8 0 3 1 3 2.7"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * The Domains item, with how many are waiting for a decision.
 *
 * The count is the point: a domain nobody has looked at is a business whose
 * people cannot see each other's reports yet, and the sidebar is where an
 * administrator will notice that without going looking.
 */
function DomainsNav(): JSX.Element {
  const domains = useQuery({ queryKey: ['client-domains'], queryFn: () => api.clientDomains() });
  const waiting = domains.data?.pending ?? 0;
  return (
    <NavLink
      to="/domains"
      className="nav-item"
      title={waiting > 0 ? `Domains \u2014 ${waiting} awaiting approval` : 'Domains'}
    >
      <GlobeIcon />
      <span className="nav-label">Domains</span>
      {waiting > 0 ? (
        <span className="nav-count" aria-label={`${waiting} awaiting approval`}>
          {waiting}
        </span>
      ) : null}
    </NavLink>
  );
}

function GlobeIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="5.75" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path
        d="M2.5 8h11M8 2.25c1.6 1.6 2.4 3.5 2.4 5.75S9.6 12.15 8 13.75M8 2.25C6.4 3.85 5.6 5.75 5.6 8s.8 4.15 2.4 5.75"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
    </svg>
  );
}

function ChartIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M2.5 13.5h11M4.5 11V7m3.5 4V3.5M11.5 11V8.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** The chevron points the way the sidebar will move, not the way it is. */
function RailIcon({ railed }: { readonly railed: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d={railed ? 'M6 3.5 10.5 8 6 12.5' : 'M10 3.5 5.5 8 10 12.5'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M8 3.5v9M3.5 8h9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function ChevronIcon({ open }: { readonly open: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="12"
      height="12"
      aria-hidden="true"
      focusable="false"
      className="chevron"
      data-open={open ? 'true' : 'false'}
    >
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function CogIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="2.1" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path
        d="M8 1.6v1.6M8 12.8v1.6M1.6 8h1.6M12.8 8h1.6M3.5 3.5l1.1 1.1M11.4 11.4l1.1 1.1M12.5 3.5l-1.1 1.1M4.6 11.4l-1.1 1.1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * The title, and the one-line explanation under it.
 *
 * The explanation is not filler. "Queue" and "Board" do not tell a new triager
 * what they are looking at or what order it is in, and the header is the one
 * place to say so without spending a row of the screen on a tip nobody reads
 * twice.
 */
type PageHeading = { readonly title: string; readonly blurb: string };

function pageFor(pathname: string): PageHeading {
  if (pathname.startsWith('/queue')) {
    return {
      title: 'Queue',
      blurb: 'Everything clients have sent, across every project you can see.',
    };
  }
  if (pathname.startsWith('/board')) {
    return {
      title: 'Board',
      blurb: 'One project, by workflow state. Drag a card, or pick it up with the space bar.',
    };
  }
  if (pathname.startsWith('/tickets')) {
    return {
      title: 'Ticket',
      blurb: 'What was reported, what the triage bot made of it, and everything since.',
    };
  }
  if (pathname === '/projects/new') {
    return { title: 'New project', blurb: 'Connect a site and start collecting feedback from it.' };
  }
  if (pathname.startsWith('/projects')) {
    return { title: 'Project settings', blurb: 'Sites, widget health, and how this project ends.' };
  }
  if (pathname.startsWith('/new')) {
    return { title: 'Raise a ticket', blurb: 'For work that did not arrive through a widget.' };
  }
  if (pathname.startsWith('/domains')) {
    return {
      title: 'Domains',
      blurb: 'Client businesses, and which of their people see each other\u2019s reports.',
    };
  }
  if (pathname.startsWith('/analytics')) {
    return { title: 'Analytics', blurb: 'Where the tickets come from, and how long they sit.' };
  }
  return { title: 'Relay', blurb: 'Feedback from the sites you run, triaged and tracked.' };
}
